import { useState, useEffect } from "react";
import api from "./apiClient";
import { AxiosRequestConfig, AxiosError, AxiosResponse } from "axios";

interface UseApiResult<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

/**
 * Custom hook for making API requests with our configured axios instance
 * @param url - API endpoint
 * @param options - Axios request config options
 * @param immediate - Whether to fetch data immediately when component mounts
 */
export function useApi<T = any>(
    url: string,
    options: AxiosRequestConfig = {},
    immediate = true
): UseApiResult<T> {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState<boolean>(immediate);
    const [error, setError] = useState<string | null>(null);

    // Function to fetch data from API
    const fetchData = async (): Promise<void> => {
        setLoading(true);
        setError(null);

        try {
            const response: AxiosResponse<T> = await api({
                url,
                ...options,
            });

            setData(response.data);
        } catch (err) {
            const axiosError = err as AxiosError<{ detail?: string }>;
            const errorMessage =
                axiosError.response?.data?.detail ||
                axiosError.message ||
                "An error occurred";
            setError(errorMessage);
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data on mount if immediate is true
    useEffect(() => {
        if (immediate) {
            fetchData();
        }
    }, [url]);

    // Return data, loading state, error, and refetch function
    return { data, loading, error, refetch: fetchData };
}

export default useApi;
